import type { CameraState, RenderObject, Vec3 } from '@v2/contracts'
import { compose4, composeQuaternion4, dot3, quaternionFromBasis, scale3, transformDirection3 } from '@v2/math'

const DISTANCE = 1.5
const WIDTH_FRACTION = 0.82
const HEIGHT_FRACTION = 0.85

/**
 * A disposable, enlarged copy of a currently visible component. The caller supplies
 * only the current viewer's rendered objects, whose hidden artwork is already
 * sanitized. No identity or artwork survives into another call or projection.
 */
export function resolveInspection(
  objects: readonly RenderObject[], inspectedId: string | null, camera: CameraState,
  width: number, height: number,
): RenderObject | null {
  if (!inspectedId || !(width > 0 && height > 0) || !Number.isFinite(width + height)) return null
  const source = objects.find(object => object.id === inspectedId)
  if (!source?.inspectable || source.decorative || source.size.some(value => !(value > 0) || !Number.isFinite(value))) return null
  const { azimuth: a, elevation: e, distance, target, fov } = camera
  if (!(fov > 0 && fov < Math.PI) || ![a, e, distance, ...target].every(Number.isFinite)) return null
  const right: Vec3 = [Math.cos(a), 0, -Math.sin(a)]
  const up: Vec3 = [-Math.sin(a) * Math.sin(e), Math.cos(e), -Math.cos(a) * Math.sin(e)]
  const back: Vec3 = [Math.sin(a) * Math.cos(e), Math.sin(e), Math.cos(a) * Math.cos(e)]
  const printed = source.shape === 'card' || source.shape === 'board' || source.shape === 'slab' || source.shape === 'disc'
  // Printed faces use local +Y; their title points along local -Z. Solid pieces
  // keep their original 3D orientation so a pawn remains visibly dimensional.
  const orientation = printed ? quaternionFromBasis(right, back, scale3(up, -1)) : source.orientation
  const rotation = printed ? 0 : source.rotation
  const model = orientation
    ? composeQuaternion4([0, 0, 0], orientation, source.size)
    : compose4([0, 0, 0], rotation, source.size)
  const verticalLimit = Math.tan(fov / 2) * HEIGHT_FRACTION
  const horizontalLimit = Math.tan(fov / 2) * width / height * WIDTH_FRACTION
  let scale = Infinity
  for (const x of [-0.5, 0.5]) for (const y of [-0.5, 0.5]) for (const z of [-0.5, 0.5]) {
    const corner = transformDirection3([x, y, z], model)
    const depth = dot3(corner, back)
    // Fit the closest corners as well as the center plane. Ignoring thickness
    // would crop thick tokens or tilted solid pieces under perspective.
    const horizontal = Math.abs(dot3(corner, right)) + horizontalLimit * depth
    const vertical = Math.abs(dot3(corner, up)) + verticalLimit * depth
    if (horizontal > 0) scale = Math.min(scale, horizontalLimit * DISTANCE / horizontal)
    if (vertical > 0) scale = Math.min(scale, verticalLimit * DISTANCE / vertical)
    if (depth > 0) scale = Math.min(scale, (DISTANCE - 0.15) / depth)
  }
  if (!(scale > 0) || !Number.isFinite(scale)) return null
  return {
    ...source,
    id: '__table:inspection',
    surface: undefined,
    dimmed: false,
    position: [target[0] + back[0] * (distance - DISTANCE), target[1] + back[1] * (distance - DISTANCE), target[2] + back[2] * (distance - DISTANCE)],
    rotation,
    orientation,
    size: scale3(source.size, scale),
    layer: 'foreground',
    decorative: true,
    interactive: false,
    inspectable: false,
    highlighted: false,
  }
}
